import React, { useState } from 'react';
import { MessageSquare, X, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '../services/supabase';

interface FeedbackButtonProps {
  userEmail?: string;
  userId?: string;
  userName?: string;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ userEmail, userId, userName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: dbError } = await supabase.from('feedback').insert({
        user_email: userEmail || null,
        user_id: userId || null,
        message: message.trim(),
      });
      if (dbError) throw dbError;

      // Send email notification — await so errors are visible in console
      try {
        const r = await fetch('/api/beta/notify-feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, userName, message: message.trim() }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          console.error('notify-feedback failed:', r.status, body);
        }
      } catch (e) {
        console.error('notify-feedback network error:', e);
      }

      setSubmitted(true);
      setMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitted(false);
    setError(null);
    setMessage('');
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-2 right-5 z-50 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-full shadow-md transition-all active:scale-95"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Feedback
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
              <h2 className="font-bold text-lg text-slate-900">Beta Feedback</h2>
              <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5">
              {submitted ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                  <p className="font-bold text-slate-800 text-lg">Thanks for your feedback!</p>
                  <p className="text-sm text-slate-500">Your message has been received.</p>
                  <button
                    onClick={handleClose}
                    className="mt-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition-colors active:scale-95"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-700 mb-4 leading-relaxed">
                    Thank you for beta testing SideOutIQ! Please share any issues you've found or
                    feature improvements you would like and I will add them as soon as I can.
                    Thanks, Dean.
                  </p>
                  <div className="relative">
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value.slice(0, 500))}
                      placeholder="Share your feedback..."
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
                      rows={5}
                      autoFocus
                    />
                    <span className={`absolute bottom-2.5 right-3 text-[11px] font-medium ${message.length >= 480 ? 'text-amber-500' : 'text-slate-400'}`}>
                      {message.length}/500
                    </span>
                  </div>
                  {error && (
                    <p className="mt-2 text-xs text-red-600 font-medium">{error}</p>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={!message.trim() || isSubmitting}
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-blue-600/20"
                  >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
